import { useCallback, useEffect, useRef } from 'react';
import { Dialog as DialogType, DialogSection, DialogField, DialogVariable, DialogDataContent, getCachedDialogs } from '@/lib/stores/dialogStore';
import { Node, Edge } from '@xyflow/react';
import { dialogFlowConfig } from   '@/components/flow/configs/dialogFlowConfig';
import { generateId, deepClone, migrateDialogSectionsToNodes, generateSectionIdentifier } from '../utils/dialogUtils';
import { ViewMode, ContextMenuState, PendingJumpConnection, SpeakerMode, DialogData } from '../types';
import { useZoom } from '@/providers/ZoomProvider';
import { updateStringSourceTextLocally } from '@/lib/stores/stringsStore';

export interface UseDialogFlowHandlersProps {
  // State
  effectiveGameId: string;
  initialData?: DialogData | null;
  dialogName: string;
  dialogKey: string;
  description: string;
  translatable: boolean;
  speakerMode: SpeakerMode;
  speakerTypeId: string | null;
  speakerTemplateIds: string[];
  dialogVariables: DialogVariable[];
  viewMode: ViewMode;
  flowNodes: Node[];
  flowEdges: Edge[];
  localFlowSections: DialogSection[];
  flowInitialized: boolean;
  contextMenu: ContextMenuState;
  pendingJumpConnection: PendingJumpConnection | null;
  
  // Setters
  setFlowNodes: React.Dispatch<React.SetStateAction<Node[]>>;
  setFlowEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
  setLocalFlowSections: React.Dispatch<React.SetStateAction<DialogSection[]>>;
  setHasUnsavedChanges: (val: boolean) => void;
  setContextMenu: React.Dispatch<React.SetStateAction<ContextMenuState>>;
  setDialogJumpSelectorOpen: (val: boolean) => void;
  setPendingJumpConnection: (conn: PendingJumpConnection | null) => void;
  
  // Refs
  sectionsRef: React.MutableRefObject<DialogSection[]>;
  localFlowSectionsRef: React.MutableRefObject<DialogSection[]>;
  flowNodesRef: React.MutableRefObject<Node[]>;
  regenerateFlowFromSectionsRef: React.MutableRefObject<(() => void) | undefined>;
  deletedStringIdsRef: React.MutableRefObject<Set<string>>;
  
  // Toast
  toast: (options: { title: string; description: string; variant?: 'default' | 'destructive' }) => void;
}

export interface DialogFlowHandlers {
  handleFieldUpdate: (sectionIndex: number, fieldIndex: number, updates: Partial<DialogField>) => void;
  handleSectionUpdate: (sectionIndex: number, updates: { name: string }) => void;
  regenerateFlowFromSections: () => void;
  handleConnect: (connection: any) => void;
  handleNodesChange: (nodes: Node[]) => void;
  handleEdgesChange: (edges: Edge[]) => void;
  handleConnectionEnd: (event: any, connectionState: any, flowPosition?: { x: number; y: number }) => void;
  handlePaneContextMenu: (event: React.MouseEvent, flowPosition: { x: number; y: number }) => void;
  handleCreateFieldFromConnection: (fieldType: string) => void;
  handleOpenDialogJumpSelector: () => void;
  handleDialogJumpConfirm: (dialogId: string, sectionIdentifier?: string | null) => void;
  handleDragOver: (event: React.DragEvent) => void;
  handleDrop: (event: React.DragEvent, nodes: Node[]) => Node | null;
  addCallbackToNodes: (nodes: Node[]) => Node[];
}

/**
 * Custom hook for flow-related handlers in DialogWizard
 */
export function useDialogFlowHandlers({
  effectiveGameId,
  initialData,
  dialogName,
  dialogKey,
  description,
  translatable,
  speakerMode,
  speakerTypeId,
  speakerTemplateIds,
  dialogVariables,
  viewMode,
  flowNodes,
  flowEdges,
  localFlowSections,
  flowInitialized,
  contextMenu,
  pendingJumpConnection,
  setFlowNodes,
  setFlowEdges,
  setLocalFlowSections,
  setHasUnsavedChanges,
  setContextMenu,
  setDialogJumpSelectorOpen,
  setPendingJumpConnection,
  sectionsRef,
  localFlowSectionsRef,
  flowNodesRef,
  regenerateFlowFromSectionsRef,
  deletedStringIdsRef,
  toast,
}: UseDialogFlowHandlersProps): DialogFlowHandlers {
  const { zoom } = useZoom();
  const pendingNodePositionsRef = useRef<Record<string, { x: number; y: number }>>({});
  const flowMetadataRef = useRef<{ edgeStyles?: { [edgeId: string]: { type?: string; animated?: boolean } } }>({});
  const flowEdgesRef = useRef<Edge[]>(flowEdges);
  
  // Cache migrated data to avoid re-migrating every frame
  // Use a cache key based on sections length and IDs rather than reference equality
  const migratedDataCacheRef = useRef<{ 
    cacheKey: string; 
    migratedData: DialogDataContent 
  } | null>(null);

  // Initialize flowMetadata from initialData
  useEffect(() => {
    const flowMetadata = initialData?.data?.flowMetadata;
    if (flowMetadata) {
      flowMetadataRef.current = flowMetadata;
    }
  }, [initialData?.id, initialData?.data?.flowMetadata]); // Only run when dialog ID or flowMetadata changes

  // Keep flowEdgesRef in sync with flowEdges
  useEffect(() => {
    flowEdgesRef.current = flowEdges;
  }, [flowEdges]);

  // Handle field updates from the flow view
  const handleFieldUpdate = useCallback((sectionIndex: number, fieldIndex: number, updates: Partial<DialogField>) => {
    const oldField = localFlowSectionsRef.current[sectionIndex]?.fields[fieldIndex];
    if (!oldField) return;
    
    // Check if content actually changed - be very strict
    const textChanged = (updates.text !== undefined && oldField.text !== updates.text);
    const typeChanged = (updates.type !== undefined && oldField.type !== updates.type);
    const optionsChanged = (updates.options !== undefined && JSON.stringify(oldField.options) !== JSON.stringify(updates.options));
    const conditionChanged = (updates.condition !== undefined && JSON.stringify(oldField.condition) !== JSON.stringify(updates.condition));
    const conditionOptionsChanged = (updates.conditionOptions !== undefined && JSON.stringify(oldField.conditionOptions) !== JSON.stringify(updates.conditionOptions));
    const setterChanged = (updates.setter !== undefined && JSON.stringify(oldField.setter) !== JSON.stringify(updates.setter));
    const hasContentChanged = textChanged || typeChanged || optionsChanged || conditionChanged || conditionOptionsChanged || setterChanged;

    if (!hasContentChanged) {
      // Content identical - skip all updates to prevent unnecessary re-renders
      return;
    }

    // Keep local stringsStore in sync so edit mode sees the latest text
    if (textChanged && oldField.string_id && updates.text !== undefined) {
      updateStringSourceTextLocally(oldField.string_id, updates.text);
    }

    const newSections = deepClone(localFlowSectionsRef.current);

    if (newSections[sectionIndex] && newSections[sectionIndex].fields[fieldIndex]) {
      newSections[sectionIndex].fields[fieldIndex] = {
        ...newSections[sectionIndex].fields[fieldIndex],
        ...updates,
      };
    }

    // Update the ref immediately so regeneration uses the latest data
    localFlowSectionsRef.current = newSections;
    
    // Update state
    setLocalFlowSections(newSections);
    setHasUnsavedChanges(true);

    // OPTIMIZATION: Instead of full regeneration, directly update the specific node's field data
    // This preserves node UI state (like editing mode) and avoids unnecessary re-renders
    const updatedField = newSections[sectionIndex]?.fields[fieldIndex];
    if (updatedField) {
      // CRITICAL: Use flowNodesRef.current to get the LATEST nodes
      // Props may be stale when this callback executes
      const currentNodes = flowNodesRef.current;
      const targetNode = currentNodes.find(
        node => node.data.nodeIndex === sectionIndex && node.data.fieldIndex === fieldIndex
      );
      
      if (targetNode) {
        const currentField = targetNode.data.field as DialogField;
        const hasChanged = 
          currentField.text !== updatedField.text ||
          currentField.type !== updatedField.type ||
          JSON.stringify(currentField.options) !== JSON.stringify(updatedField.options) ||
          JSON.stringify(currentField.condition) !== JSON.stringify(updatedField.condition) ||
          JSON.stringify(currentField.conditionOptions) !== JSON.stringify(updatedField.conditionOptions) ||
          JSON.stringify(currentField.setter) !== JSON.stringify(updatedField.setter);
        
        // CRITICAL: Only call setFlowNodes if content actually changed
        // This prevents React from re-rendering and resetting editing state
        if (hasChanged) {
          setFlowNodes(prevNodes => 
            prevNodes.map(node => {
              if (node.data.nodeIndex === sectionIndex && node.data.fieldIndex === fieldIndex) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    field: updatedField,
                    // Keep data.node in sync with data.field so that BaseDialogNode
                    // (which reads data.node for visual styling like color and type label)
                    // reflects the updated type immediately
                    node: updatedField,
                  },
                };
              }
              return node;
            })
          );
        }
      }
    }

    // Invalidate cache so next full regeneration gets fresh data
    migratedDataCacheRef.current = null;
  }, [setLocalFlowSections, setHasUnsavedChanges, setFlowNodes, localFlowSectionsRef]);
  
  // Handle section updates from the flow view
  const handleSectionUpdate = useCallback((sectionIndex: number, updates: { name: string }) => {
    
    const newSections = deepClone(localFlowSectionsRef.current);
    
    if (newSections[sectionIndex]) {
      newSections[sectionIndex] = {
        ...newSections[sectionIndex],
        ...updates,
      };
    }
    
    // Update the ref immediately so regeneration uses the latest data
    localFlowSectionsRef.current = newSections;
    
    // Note: No need to invalidate cache - the cache key will automatically detect the change
    
    // Update state
    setLocalFlowSections(newSections);
    setHasUnsavedChanges(true);
    
    // Regenerate flow immediately using the updated ref
    if (regenerateFlowFromSectionsRef.current) {
      regenerateFlowFromSectionsRef.current();
    }
  }, [setLocalFlowSections, setHasUnsavedChanges, localFlowSectionsRef, regenerateFlowFromSectionsRef]);
  
  // Memoized function to add field and section update callbacks to nodes
  const addCallbackToNodes = useCallback((nodes: Node[]) => {
    return nodes.map(node => ({
      ...node,
      data: {
        ...node.data,
        onFieldUpdate: handleFieldUpdate,
        onSectionUpdate: handleSectionUpdate,
      },
    }));
  }, [handleFieldUpdate, handleSectionUpdate]);

  // Helper function to regenerate flow from sections data
  const regenerateFlowFromSections = useCallback(() => {
    if (!effectiveGameId) return;
    
    const currentLocalFlowSections = localFlowSectionsRef.current;
    const currentSections = sectionsRef.current;
    const sectionsToVisualize = viewMode === 'flow' && currentLocalFlowSections.length > 0 ? currentLocalFlowSections : currentSections;
    
    // CRITICAL FIX: Use flowNodesRef.current to get the LATEST node positions
    // This prevents race conditions when nodes are moved and then a field is edited
    const currentFlowNodes = flowNodesRef.current;
    
    
    // Generate a stable cache key based on sections structure AND content
    // Include section name, field text, type, options, condition, conditionOptions, and setter to detect content changes
    const generateCacheKey = (sections: DialogSection[]): string => {
      return sections
        .map(s => `${s.id}:${s.name}:${s.fields?.map(f => {
          const optionsHash = f.options?.map(o => `${o.option_text}:${o.next_section || ''}:${o.next_dialog || ''}`).join(',') || '';
          const conditionHash = f.condition ? `cond:${f.condition.variable || ''}:${f.condition.checkVariable || ''}` : '';
          const conditionOptionsHash = f.conditionOptions?.map(co => `${co.operator}:${co.condition_value}:${co.next_section || ''}:${co.next_dialog || ''}`).join(',') || '';
          const setterHash = f.setter ? `setter:${f.setter.variable}:${f.setter.operator}:${f.setter.value}` : '';
          return `${f.id}:${f.type}:${f.text}:${optionsHash}:${conditionHash}:${conditionOptionsHash}:${setterHash}:${f.next_section || ''}:${f.next_field || ''}`;
        }).join('|') || ''}`)
        .join('::');
    };
    
    const currentCacheKey = generateCacheKey(sectionsToVisualize);
    
    // Check if we can reuse cached migrated data
    let migratedData: DialogDataContent;
    const cachedData = migratedDataCacheRef.current;
    
    if (cachedData && cachedData.cacheKey === currentCacheKey) {
      // Sections haven't changed structurally, reuse cached migrated data
      migratedData = cachedData.migratedData;
    } else {
      // Sections changed or no cache, perform migration
      // CRITICAL: Never pass existing nodes - always regenerate from sections
      // This ensures field content updates are reflected in nodes
      const dataToMigrate: DialogDataContent = {
        sections: sectionsToVisualize,
        nodes: [], // Always empty - force migration to regenerate from sections
        translatable,
        speakerMode,
        speakerTypeId,
        speakerTemplateIds,
        variables: dialogVariables,
        flowMetadata: flowMetadataRef.current,
      };
      
      // Migrate sections to nodes structure
      migratedData = migrateDialogSectionsToNodes(dataToMigrate);
      
      // Cache the migrated data for future regenerations
      migratedDataCacheRef.current = {
        cacheKey: currentCacheKey,
        migratedData: migratedData,
      };
      
    }
    
    const dialogForFlow: DialogType = {
      id: initialData?.id || '',
      game_id: effectiveGameId,
      name: dialogName,
      dialog_key: dialogKey,
      description,
      tags: initialData?.tags || [],
      folder: initialData?.folder,
      status: (initialData?.status as 'draft' | 'published') || 'draft',
      data: migratedData,
      created_at: initialData?.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    
    const generatedNodes = dialogFlowConfig.dataToNodes(dialogForFlow);
    const generatedEdges = dialogFlowConfig.dataToEdges(dialogForFlow);
    
    // Preserve existing node positions when regenerating
    const pendingPositions = pendingNodePositionsRef.current;
    
    // Helper to extract field/node ID from node ID
    // Supports both formats:
    // - OLD: field-{sectionIndex}-{fieldIndex}-{fieldId}
    // - NEW: node-{nodeIndex}-{nodeId}
    const extractFieldId = (nodeId: string): string | null => {
      const parts = nodeId.split('-');
      // For old field format: field-{sectionIndex}-{fieldIndex}-{fieldId}
      if (parts[0] === 'field' && parts.length >= 4) {
        return parts.slice(3).join('-'); // Join remaining parts as fieldId may contain dashes
      }
      // For new node format: node-{nodeIndex}-{nodeId}
      if (parts[0] === 'node' && parts.length >= 3) {
        return parts.slice(2).join('-'); // Join remaining parts as nodeId may contain dashes
      }
      return null;
    };
    
    const nodesWithPreservedPositions = generatedNodes.map(newNode => {
      // First try exact ID match (for external nodes and when indices haven't changed)
      let existingNode = currentFlowNodes.find(n => n.id === newNode.id);
      
      // If no exact match, try matching by field ID (handles index shifts after deletion)
      if (!existingNode) {
        const newFieldId = extractFieldId(newNode.id);
        if (newFieldId) {
          existingNode = currentFlowNodes.find(n => {
            const existingFieldId = extractFieldId(n.id);
            return existingFieldId && existingFieldId === newFieldId;
          });
        }
      }
      
      const nodeWithCallbacks = {
        ...newNode,
        data: {
          ...newNode.data,
          onFieldUpdate: handleFieldUpdate,
          onSectionUpdate: handleSectionUpdate,
        },
      };
      
      if (existingNode) {
        return {
          ...nodeWithCallbacks,
          position: existingNode.position,
          width: existingNode.width,
          height: existingNode.height,
        };
      }
      if (pendingPositions[newNode.id]) {
        const position = pendingPositions[newNode.id];
        delete pendingPositions[newNode.id];
        return {
          ...nodeWithCallbacks,
          position,
        };
      }
      return nodeWithCallbacks;
    });
    
  

    setFlowNodes(nodesWithPreservedPositions);
    setFlowEdges(generatedEdges);
  }, [effectiveGameId, initialData, dialogName, dialogKey, description, viewMode, translatable, speakerMode, speakerTypeId, speakerTemplateIds, dialogVariables, handleFieldUpdate, handleSectionUpdate, localFlowSections.length, localFlowSectionsRef, sectionsRef, flowNodesRef, setFlowNodes, setFlowEdges]);
  
  // Store regenerateFlowFromSections in ref for use by handleFieldUpdate
  useEffect(() => {
    regenerateFlowFromSectionsRef.current = regenerateFlowFromSections;
  }, [regenerateFlowFromSections, regenerateFlowFromSectionsRef]);
  
  // Handle right-click on canvas
  const handlePaneContextMenu = useCallback((event: React.MouseEvent, flowPosition: { x: number; y: number }) => {
    event.preventDefault();
    const menuX = zoom ? event.clientX / zoom : event.clientX;
    const menuY = zoom ? event.clientY / zoom : event.clientY;
    setContextMenu({
      show: true,
      position: {
        x: menuX,
        y: menuY,
      },
      flowPosition,
    });
  }, [setContextMenu, zoom]);
  
  // Handle successful connection
  const handleConnect = useCallback((connection: any) => {
    
    // NEW NODES FORMAT: node-{nodeIndex}-{nodeId}
    // OLD SECTIONS FORMAT: field-{sectionIndex}-{fieldIndex}
    // Support both formats for backward compatibility
    const isNewFormat = connection.source.startsWith('node-');
    const sourceMatch = connection.source.match(/(?:node|field)-(\d+)(?:-(\d+))?/);
    if (!sourceMatch) return;
    
    const sourceSectionIndex = parseInt(sourceMatch[1]);
    // In new nodes format, each section has exactly one field at index 0
    // In old sections format, fieldIndex is captured in group 2
    const sourceFieldIndex = isNewFormat ? 0 : parseInt(sourceMatch[2] || '0');
    const sourceHandle = connection.sourceHandle;
    
    const updatedSections = deepClone(localFlowSectionsRef.current) as DialogSection[];
    const sourceField = updatedSections[sourceSectionIndex]?.fields?.[sourceFieldIndex];
    if (!sourceField) return;
    
    if (sourceHandle && sourceHandle.startsWith('option-')) {
      const optionIndex = parseInt(sourceHandle.replace('option-', ''));
      
      if (sourceField.type === 'question' && sourceField.options && sourceField.options[optionIndex]) {
        if (connection.target.startsWith('external-dialog-')) {
          const externalMatch = connection.target.match(/^external-dialog-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:-section-(.+))?$/i);
          if (externalMatch) {
            const dialogId = externalMatch[1];
            const sectionId = externalMatch[2];
            
            const dialogs = effectiveGameId ? getCachedDialogs(effectiveGameId) : [];
            const targetDialog = dialogs.find(d => d.id === dialogId);
            
            let targetSectionIdentifier = null;
            if (targetDialog && sectionId) {
              const matchingSection = targetDialog.data?.sections?.find(s => s.section_identifier === sectionId);
              if (matchingSection) {
                targetSectionIdentifier = sectionId;
              }
            }
            
            sourceField.options[optionIndex] = {
              ...sourceField.options[optionIndex],
              next_dialog: dialogId,
              next_dialog_section: targetSectionIdentifier,
              next_section: null,
            };
            
            setLocalFlowSections(updatedSections);
            localFlowSectionsRef.current = updatedSections;
            setHasUnsavedChanges(true);
            // Regenerate immediately (not with setTimeout) to ensure edges get proper styling
            regenerateFlowFromSections();
            
            toast({
              title: 'Connection Created',
              description: `Option "${sourceField.options[optionIndex].option_text}" now jumps to ${targetDialog?.name || 'external dialog'}`,
            });
          }
        } else {
          // Parse target node ID
          // NEW FORMAT: node-{nodeIndex}-{nodeId} e.g., node-17-1770284330233-57dwiy8ec
          // OLD FORMAT: field-{sectionIndex}-{fieldIndex}-{fieldId} e.g., field-17-0-someFieldId
          const isTargetNewFormat = connection.target.startsWith('node-');
          let targetSectionIndex, targetFieldIndex, targetFieldId;
          
          if (isTargetNewFormat) {
            // New format: extract nodeIndex and full nodeId
            const match = connection.target.match(/node-(\d+)-(.+)/);
            if (!match) return;
            targetSectionIndex = parseInt(match[1]);
            targetFieldId = match[2]; // Full node ID (e.g., 1770284330233-57dwiy8ec)
            targetFieldIndex = 0; // Always 0 in new format
          } else {
            // Old format: extract sectionIndex, fieldIndex, and fieldId
            const match = connection.target.match(/field-(\d+)-(\d+)-(.+)/);
            if (!match) return;
            targetSectionIndex = parseInt(match[1]);
            targetFieldIndex = parseInt(match[2]);
            targetFieldId = match[3];
          }
          
          const targetSection = updatedSections[targetSectionIndex];
          if (!targetSection) return;
          
          // Store both section and field for precise navigation
          sourceField.options[optionIndex] = {
            ...sourceField.options[optionIndex],
            next_section: targetSection.section_identifier,
            next_field: targetFieldId,
            next_dialog: null,
            next_dialog_section: null,
          };
          
          setLocalFlowSections(updatedSections);
          localFlowSectionsRef.current = updatedSections;
          setHasUnsavedChanges(true);
          // Regenerate immediately (not with setTimeout) to ensure edges get proper styling
          regenerateFlowFromSections();
          
          const targetField = targetSection.fields?.[targetFieldIndex];
          const targetDescription = targetFieldIndex === 0 
            ? `${targetSection.name}` 
            : `${targetSection.name} (field ${targetFieldIndex + 1})`;
          
          toast({
            title: 'Connection Created',
            description: `Option "${sourceField.options[optionIndex].option_text}" now leads to ${targetDescription}`,
          });
        }
      }
    } else if (sourceHandle === 'right') {
      if (connection.target.startsWith('external-dialog-')) {
        const externalMatch = connection.target.match(/^external-dialog-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:-section-(.+))?$/i);
        if (externalMatch) {
          const dialogId = externalMatch[1];
          const sectionId = externalMatch[2];
          
          const dialogs = effectiveGameId ? getCachedDialogs(effectiveGameId) : [];
          const targetDialog = dialogs.find(d => d.id === dialogId);
          
          let targetSectionIdentifier = null;
          if (targetDialog && sectionId) {
            const matchingSection = targetDialog.data?.sections?.find(s => s.section_identifier === sectionId);
            if (matchingSection) {
              targetSectionIdentifier = sectionId;
            }
          }
          
          sourceField.next_dialog = dialogId;
          sourceField.next_dialog_section = targetSectionIdentifier;
          sourceField.next_section = null;
          
          setLocalFlowSections(updatedSections);
          localFlowSectionsRef.current = updatedSections;
          setHasUnsavedChanges(true);
          // Regenerate immediately (not with setTimeout) to ensure edges get proper styling
          regenerateFlowFromSections();
          
          toast({
            title: 'Connection Created',
            description: `Field now jumps to ${targetDialog?.name || 'external dialog'}`,
          });
        }
        } else {
          // Parse target node ID
          // NEW FORMAT: node-{nodeIndex}-{nodeId} e.g., node-17-1770284330233-57dwiy8ec
          // OLD FORMAT: field-{sectionIndex}-{fieldIndex}-{fieldId} e.g., field-17-0-someFieldId
          const isTargetNewFormat = connection.target.startsWith('node-');
          let targetSectionIndex, targetFieldIndex, targetFieldId;
          
          if (isTargetNewFormat) {
            // New format: extract nodeIndex and full nodeId
            const match = connection.target.match(/node-(\d+)-(.+)/);
            if (!match) return;
            targetSectionIndex = parseInt(match[1]);
            targetFieldId = match[2]; // Full node ID (e.g., 1770284330233-57dwiy8ec)
            targetFieldIndex = 0; // Always 0 in new format
          } else {
            // Old format: extract sectionIndex, fieldIndex, and fieldId
            const match = connection.target.match(/field-(\d+)-(\d+)-(.+)/);
            if (!match) return;
            targetSectionIndex = parseInt(match[1]);
            targetFieldIndex = parseInt(match[2]);
            targetFieldId = match[3];
          }
          
          const targetSection = updatedSections[targetSectionIndex];
          if (!targetSection) return;
          
          // Store both section and field for precise navigation
          sourceField.next_section = targetSection.section_identifier;
          sourceField.next_field = targetFieldId;
          sourceField.next_dialog = null;
          sourceField.next_dialog_section = null;
          
          setLocalFlowSections(updatedSections);
          localFlowSectionsRef.current = updatedSections;
          setHasUnsavedChanges(true);
          // Regenerate immediately (not with setTimeout) to ensure edges get proper styling
          regenerateFlowFromSections();
          
          const targetDescription = targetFieldIndex === 0 
            ? `${targetSection.name}` 
            : `${targetSection.name} (field ${targetFieldIndex + 1})`;
          
          toast({
            title: 'Connection Created',
            description: `Field now leads to ${targetDescription}`,
          });
        }
    } else if (sourceHandle?.startsWith('cond-option-')) {
      // Handle new conditionOptions format
      const optionIndex = parseInt(sourceHandle.replace('cond-option-', ''), 10);
      
      if (sourceField && sourceField.conditionOptions && sourceField.conditionOptions[optionIndex]) {
        if (connection.target.startsWith('external-dialog-')) {
          // External dialog connection
          const externalMatch = connection.target.match(/^external-dialog-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:-section-(.+))?$/i);
          if (externalMatch) {
            const dialogId = externalMatch[1];
            const sectionId = externalMatch[2];
            
            sourceField.conditionOptions[optionIndex].next_dialog = dialogId;
            sourceField.conditionOptions[optionIndex].next_dialog_section = sectionId || null;
            sourceField.conditionOptions[optionIndex].next_section = null;
            
            setLocalFlowSections(updatedSections);
            localFlowSectionsRef.current = updatedSections;
            setHasUnsavedChanges(true);
            // Regenerate immediately (not with setTimeout) to ensure edges get proper styling
            regenerateFlowFromSections();
            
            toast({
              title: 'Connection Created',
              description: `Condition option now jumps to external dialog`,
            });
          }
        } else {
          // Internal section/field connection
          // Parse target node ID
          // NEW FORMAT: node-{nodeIndex}-{nodeId}
          // OLD FORMAT: field-{sectionIndex}-{fieldIndex}-{fieldId}
          const targetMatch = connection.target.match(/(?:node|field)-(\d+)-(\d+)-(.+)/);
          if (!targetMatch) return;
          
          const targetSectionIndex = parseInt(targetMatch[1], 10);
          const targetFieldIndex = parseInt(targetMatch[2], 10);
          const targetFieldId = targetMatch[3];
          const targetSection = updatedSections[targetSectionIndex];
          
          if (targetSection) {
            // Store both section and field for precise navigation
            sourceField.conditionOptions[optionIndex].next_section = targetSection.section_identifier;
            sourceField.conditionOptions[optionIndex].next_field = targetFieldId;
            sourceField.conditionOptions[optionIndex].next_dialog = null;
            sourceField.conditionOptions[optionIndex].next_dialog_section = null;
            
            setLocalFlowSections(updatedSections);
            localFlowSectionsRef.current = updatedSections;
            setHasUnsavedChanges(true);
            // Regenerate immediately (not with setTimeout) to ensure edges get proper styling
            regenerateFlowFromSections();
            
            const targetDescription = targetFieldIndex === 0 
              ? `${targetSection.name}` 
              : `${targetSection.name} (field ${targetFieldIndex + 1})`;
            
            toast({
              title: 'Connection Created',
              description: `Condition option now leads to ${targetDescription}`,
            });
          }
        }
      }
    } else if (sourceHandle === 'next') {
      // Handle setter node connections (setter nodes use 'next' handle)
      if (connection.target.startsWith('external-dialog-')) {
        const externalMatch = connection.target.match(/^external-dialog-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:-section-(.+))?$/i);
        if (externalMatch) {
          const dialogId = externalMatch[1];
          const sectionId = externalMatch[2];
          
          const dialogs = effectiveGameId ? getCachedDialogs(effectiveGameId) : [];
          const targetDialog = dialogs.find(d => d.id === dialogId);
          
          let targetSectionIdentifier = null;
          if (targetDialog && sectionId) {
            const matchingSection = targetDialog.data?.sections?.find(s => s.section_identifier === sectionId);
            if (matchingSection) {
              targetSectionIdentifier = sectionId;
            }
          }
          
          sourceField.next_dialog = dialogId;
          sourceField.next_dialog_section = targetSectionIdentifier;
          sourceField.next_section = null;
          
          setLocalFlowSections(updatedSections);
          localFlowSectionsRef.current = updatedSections;
          setHasUnsavedChanges(true);
          // Regenerate immediately (not with setTimeout) to ensure edges get proper styling
          regenerateFlowFromSections();
          
          toast({
            title: 'Connection Created',
            description: `Setter now jumps to ${targetDialog?.name || 'external dialog'}`,
          });
        }
      } else {
        // Parse target node ID
        // NEW FORMAT: node-{nodeIndex}-{nodeId} e.g., node-17-1770284330233-57dwiy8ec
        // OLD FORMAT: field-{sectionIndex}-{fieldIndex}-{fieldId} e.g., field-17-0-someFieldId
        const isTargetNewFormat = connection.target.startsWith('node-');
        let targetSectionIndex, targetFieldIndex, targetFieldId;
        
        if (isTargetNewFormat) {
          // New format: extract nodeIndex and full nodeId
          const match = connection.target.match(/node-(\d+)-(.+)/);
          if (!match) return;
          targetSectionIndex = parseInt(match[1]);
          targetFieldId = match[2]; // Full node ID (e.g., 1770284330233-57dwiy8ec)
          targetFieldIndex = 0; // Always 0 in new format
        } else {
          // Old format: extract sectionIndex, fieldIndex, and fieldId
          const match = connection.target.match(/field-(\d+)-(\d+)-(.+)/);
          if (!match) return;
          targetSectionIndex = parseInt(match[1]);
          targetFieldIndex = parseInt(match[2]);
          targetFieldId = match[3];
        }
        
        const targetSection = updatedSections[targetSectionIndex];
        if (!targetSection) return;
        
        // Store both section and field for precise navigation
        sourceField.next_section = targetSection.section_identifier;
        sourceField.next_field = targetFieldId;
        sourceField.next_dialog = null;
        sourceField.next_dialog_section = null;
        
        setLocalFlowSections(updatedSections);
        localFlowSectionsRef.current = updatedSections;
        setHasUnsavedChanges(true);
        // Regenerate immediately (not with setTimeout) to ensure edges get proper styling
        regenerateFlowFromSections();
        
        const targetDescription = targetFieldIndex === 0 
          ? `${targetSection.name}` 
          : `${targetSection.name} (field ${targetFieldIndex + 1})`;
        
        toast({
          title: 'Connection Created',
          description: `Setter now leads to ${targetDescription}`,
        });
      }
    } else if (sourceHandle === 'true' || sourceHandle === 'false') {
      // Handle condition field branches (legacy true/false)
      const isTrueBranch = sourceHandle === 'true';
      
      if (connection.target.startsWith('external-dialog-')) {
        const externalMatch = connection.target.match(/^external-dialog-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:-section-(.+))?$/i);
        if (externalMatch) {
          const dialogId = externalMatch[1];
          const sectionId = externalMatch[2];
          
          const dialogs = effectiveGameId ? getCachedDialogs(effectiveGameId) : [];
          const targetDialog = dialogs.find(d => d.id === dialogId);
          
          let targetSectionIdentifier = null;
          if (targetDialog && sectionId) {
            const matchingSection = targetDialog.data?.sections?.find(s => s.section_identifier === sectionId);
            if (matchingSection) {
              targetSectionIdentifier = sectionId;
            }
          }
          
          if (isTrueBranch) {
            sourceField.next_dialog_true = dialogId;
            sourceField.next_dialog_section_true = targetSectionIdentifier;
            sourceField.next_section_true = null;
          } else {
            sourceField.next_dialog_false = dialogId;
            sourceField.next_dialog_section_false = targetSectionIdentifier;
            sourceField.next_section_false = null;
          }
          
          setLocalFlowSections(updatedSections);
          localFlowSectionsRef.current = updatedSections;
          setHasUnsavedChanges(true);
          // Regenerate immediately (not with setTimeout) to ensure edges get proper styling
          regenerateFlowFromSections();
          
          toast({
            title: 'Connection Created',
            description: `${isTrueBranch ? 'True' : 'False'} branch now jumps to ${targetDialog?.name || 'external dialog'}`,
          });
        }
      } else {
        // Parse target node ID
        // NEW FORMAT: node-{nodeIndex}-{nodeId} e.g., node-17-1770284330233-57dwiy8ec
        // OLD FORMAT: field-{sectionIndex}-{fieldIndex}-{fieldId} e.g., field-17-0-someFieldId
        const isTargetNewFormat = connection.target.startsWith('node-');
        let targetSectionIndex, targetFieldIndex, targetFieldId;
        
        if (isTargetNewFormat) {
          // New format: extract nodeIndex and full nodeId
          const match = connection.target.match(/node-(\d+)-(.+)/);
          if (!match) return;
          targetSectionIndex = parseInt(match[1]);
          targetFieldId = match[2]; // Full node ID (e.g., 1770284330233-57dwiy8ec)
          targetFieldIndex = 0; // Always 0 in new format
        } else {
          // Old format: extract sectionIndex, fieldIndex, and fieldId
          const match = connection.target.match(/field-(\d+)-(\d+)-(.+)/);
          if (!match) return;
          targetSectionIndex = parseInt(match[1]);
          targetFieldIndex = parseInt(match[2]);
          targetFieldId = match[3];
        }
        
        const targetSection = updatedSections[targetSectionIndex];
        if (!targetSection) return;
        
        // Note: Legacy true/false branches don't have next_field support
        // They use section-level navigation only
        if (isTrueBranch) {
          sourceField.next_section_true = targetSection.section_identifier;
          sourceField.next_dialog_true = null;
          sourceField.next_dialog_section_true = null;
        } else {
          sourceField.next_section_false = targetSection.section_identifier;
          sourceField.next_dialog_false = null;
          sourceField.next_dialog_section_false = null;
        }
        
        setLocalFlowSections(updatedSections);
        localFlowSectionsRef.current = updatedSections;
        setHasUnsavedChanges(true);
        // Regenerate immediately (not with setTimeout) to ensure edges get proper styling
        regenerateFlowFromSections();
        
        const targetDescription = targetFieldIndex === 0 
          ? `${targetSection.name}` 
          : `${targetSection.name} (field ${targetFieldIndex + 1})`;
        
        toast({
          title: 'Connection Created',
          description: `${isTrueBranch ? 'True' : 'False'} branch now leads to ${targetDescription}`,
        });
      }
    }
  }, [setLocalFlowSections, toast, regenerateFlowFromSections, effectiveGameId, localFlowSectionsRef, setHasUnsavedChanges]);
  
  // Handle nodes changes (including deletions)
  const handleNodesChange = useCallback((nodes: Node[]) => {

    
    const currentNodeIds = new Set(flowNodesRef.current.map(n => n.id));
    const newNodeIds = new Set(nodes.map(n => n.id));
    const deletedNodeIds = Array.from(currentNodeIds).filter(id => !newNodeIds.has(id));
    
    if (deletedNodeIds.length > 0) {

      
      // Track external dialog deletions separately
      const externalDialogDeletions: Array<{ dialogId: string; nodeIdentifier?: string }> = [];
      
      const deletionsInfo = deletedNodeIds.map(nodeId => {
        // Support both new nodes format and old sections format
        // NEW FORMAT: node-{nodeIndex}-{nodeId} e.g., node-12-1770637783475-tjvi98zqy
        // OLD FORMAT: field-{sectionIndex}-{fieldIndex}-{fieldId} e.g., field-12-0-someFieldId
        // EXTERNAL DIALOG: external-dialog-{dialogId}-section-{nodeIdentifier}
        
        if (nodeId.startsWith('external-dialog-')) {
          // External dialog reference node deletion
          // Format: external-dialog-{dialogId}-section-{nodeIdentifier}
          // Or: external-dialog-{dialogId} (no section specified)
          const match = nodeId.match(/^external-dialog-([^-]+)(?:-section-(.+))?$/);
          if (match) {
            const dialogId = match[1];
            const nodeIdentifier = match[2] || undefined;
            externalDialogDeletions.push({ dialogId, nodeIdentifier });
          }
          return null; // External dialog nodes don't map to sections
        } else if (nodeId.startsWith('node-')) {
          // New format: node-{nodeIndex}-{rest of ID}
          const match = nodeId.match(/^node-(\d+)-(.+)$/);
          if (match) {
            const sectionIndex = parseInt(match[1]);
            const fieldId = match[2]; // Everything after the index is the field ID
            return {
              nodeId,
              sectionIndex,
              fieldIndex: 0, // In new format, each section has one field at index 0
              fieldId,
            };
          }
        } else if (nodeId.startsWith('field-')) {
          // Old format: field-{sectionIndex}-{fieldIndex}-{fieldId}
          const match = nodeId.match(/^field-(\d+)-(\d+)-(.+)$/);
          if (match) {
            return {
              nodeId,
              sectionIndex: parseInt(match[1]),
              fieldIndex: parseInt(match[2]),
              fieldId: match[3],
            };
          }
        }
        return null;
      }).filter(Boolean);
      
      const updatedSections = deepClone(localFlowSectionsRef.current) as DialogSection[];
      
      if (deletionsInfo.length > 0) {
        deletionsInfo.sort((a, b) => {
          if (a!.sectionIndex !== b!.sectionIndex) {
            return b!.sectionIndex - a!.sectionIndex;
          }
          return b!.fieldIndex - a!.fieldIndex;
        });
        
        deletionsInfo.forEach(info => {
          if (!info) return;
          const section = updatedSections[info.sectionIndex];
          
          if (section && section.fields) {
            // Collect string IDs from deleted fields if dialog is translatable
            if (translatable) {
              const deletedField = section.fields[info.fieldIndex];
              if (deletedField) {
                // Collect field's string_id if it exists
                if (deletedField.string_id) {
                  deletedStringIdsRef.current.add(deletedField.string_id);
                  console.log(`[handleNodesChange] Marking field string for deprecation: ${deletedField.string_id}`);
                }
                
                // Collect string IDs from options if it's a question field
                if (deletedField.type === 'question' && deletedField.options) {
                  deletedField.options.forEach(option => {
                    if (option.string_id) {
                      deletedStringIdsRef.current.add(option.string_id);
                      console.log(`[handleNodesChange] Marking option string for deprecation: ${option.string_id}`);
                    }
                  });
                }
              }
            }
            
            section.fields.splice(info.fieldIndex, 1);
            section.fields.forEach((field, idx) => {
              field.order_index = idx;
            });
            if (section.fields.length === 0) {
              updatedSections.splice(info.sectionIndex, 1);
            }
          }
        });
      }
      
      // Handle external dialog deletions
      if (externalDialogDeletions.length > 0) {
        // Find and delete all external-dialog fields that reference the deleted external dialogs
        externalDialogDeletions.forEach(({ dialogId, nodeIdentifier }) => {
          updatedSections.forEach((section, sectionIndex) => {
            if (!section.fields) return;
            
            // Filter out external-dialog fields that match the deleted external dialog
            const fieldsToDelete: number[] = [];
            section.fields.forEach((field, fieldIndex) => {
              if (field.type === 'external-dialog' && field.next_dialog === dialogId) {
                // Check if section identifier matches (or both are undefined/null)
                const fieldSection = field.next_dialog_section || undefined;
                if (fieldSection === nodeIdentifier) {
                  fieldsToDelete.push(fieldIndex);
                }
              }
            });
            
            // Delete in reverse order to maintain correct indices
            fieldsToDelete.reverse().forEach(fieldIndex => {
              section.fields!.splice(fieldIndex, 1);
            });
            
            // Reorder remaining fields
            section.fields.forEach((field, idx) => {
              field.order_index = idx;
            });
          });
        });
        
        // Remove empty sections
        for (let i = updatedSections.length - 1; i >= 0; i--) {
          if (!updatedSections[i].fields || updatedSections[i].fields!.length === 0) {
            updatedSections.splice(i, 1);
          }
        }
      }
      
      if (deletionsInfo.length > 0 || externalDialogDeletions.length > 0) {
        const validSectionIdentifiers = new Set(updatedSections.map(s => s.section_identifier));
        // Build set of valid field IDs
        const validFieldIds = new Set<string>();
        updatedSections.forEach(section => {
          section.fields?.forEach(field => validFieldIds.add(field.id));
        });
        
        updatedSections.forEach(section => {
          section.fields?.forEach(field => {
            if (field.next_section && !validSectionIdentifiers.has(field.next_section)) {
              field.next_section = null;
              field.next_field = null;
            }
            // Clear next_field if it references a deleted field
            if (field.next_field && !validFieldIds.has(field.next_field)) {
              field.next_field = null;
            }
            
            if (field.type === 'condition') {
              if (field.next_section_true && !validSectionIdentifiers.has(field.next_section_true)) {
                field.next_section_true = null;
              }
              if (field.next_section_false && !validSectionIdentifiers.has(field.next_section_false)) {
                field.next_section_false = null;
              }
              // Clear condition option next_field references
              if (field.conditionOptions) {
                field.conditionOptions.forEach(condOption => {
                  if (condOption.next_section && !validSectionIdentifiers.has(condOption.next_section)) {
                    condOption.next_section = null;
                    condOption.next_field = null;
                  }
                  if (condOption.next_field && !validFieldIds.has(condOption.next_field)) {
                    condOption.next_field = null;
                  }
                });
              }
            }
            
            if (field.type === 'question' && field.options) {
              field.options.forEach((option, optIdx) => {
                if (option.next_section && !validSectionIdentifiers.has(option.next_section)) {
                  field.options![optIdx] = { ...option, next_section: null, next_field: null };
                }
                if (option.next_field && !validFieldIds.has(option.next_field)) {
                  field.options![optIdx] = { ...field.options![optIdx], next_field: null };
                }
              });
            }
          });
        });
        
  
        
        setLocalFlowSections(updatedSections);
        localFlowSectionsRef.current = updatedSections;
        setHasUnsavedChanges(true);
        
        const totalDeleted = deletionsInfo.length + externalDialogDeletions.length;
        const description = externalDialogDeletions.length > 0
          ? `Removed ${deletionsInfo.length} field(s) and ${externalDialogDeletions.length} external dialog reference(s)`
          : `Removed ${deletionsInfo.length} field(s) from dialog data`;
        
        toast({
          title: 'Node Deleted',
          description,
        });
      }
    }
    
    // Re-attach callbacks to nodes (React Flow doesn't preserve them)
    const nodesWithCallbacks = nodes.map(node => ({
      ...node,
      data: {
        ...node.data,
        onFieldUpdate: handleFieldUpdate,
        onSectionUpdate: handleSectionUpdate,
      },
    }));
    
    // CRITICAL FIX: Update the ref immediately to ensure regenerateFlowFromSections has latest positions
    flowNodesRef.current = nodesWithCallbacks;
    
    setFlowNodes(nodesWithCallbacks);
    
    // Only set unsaved changes if we actually deleted nodes (structural change)
    // Position changes are tracked by the main useEffect via snapshot comparison
    // This prevents the save button from flashing during dragging
    if (deletedNodeIds.length > 0) {
      setHasUnsavedChanges(true);
    }
  }, [toast, setFlowNodes, setLocalFlowSections, setHasUnsavedChanges, handleFieldUpdate, handleSectionUpdate, localFlowSectionsRef, flowNodesRef, translatable, deletedStringIdsRef]);
  
  // Handle edges changes (including deletions and style changes)
  const handleEdgesChange = useCallback((edges: Edge[]) => {
    // Use ref to get the latest edges for comparison (avoid stale closure)
    const currentFlowEdges = flowEdgesRef.current;
    

    
    const currentEdgeIds = new Set(currentFlowEdges.map(e => e.id));
    const newEdgeIds = new Set(edges.map(e => e.id));
    const deletedEdgeIds = Array.from(currentEdgeIds).filter(id => !newEdgeIds.has(id));
    
    
    // Check for edge style changes (type or animated property) and store them
    const styleChanges = edges.some(newEdge => {
      const oldEdge = currentFlowEdges.find(e => e.id === newEdge.id);
      if (!oldEdge) return false;
      return oldEdge.type !== newEdge.type || oldEdge.animated !== newEdge.animated;
    });
    
    if (styleChanges) {
      // Build edge styles map
      const edgeStyles: { [edgeId: string]: { type?: string; animated?: boolean } } = {};
      edges.forEach(edge => {
        // Only store if different from default values
        if (edge.type !== 'default' || edge.animated !== true) {
          edgeStyles[edge.id] = {
            type: edge.type,
            animated: edge.animated,
          };
        }
      });
      
      // Store edge styles in flowMetadata ref for persistence
      flowMetadataRef.current = {
        ...flowMetadataRef.current,
        edgeStyles: Object.keys(edgeStyles).length > 0 ? edgeStyles : undefined,
      };
      
      setHasUnsavedChanges(true);
    }
    
    if (deletedEdgeIds.length > 0) {
      const deletedEdges = currentFlowEdges.filter(e => deletedEdgeIds.includes(e.id));
      const updatedSections = deepClone(localFlowSectionsRef.current) as DialogSection[];
      let sectionsModified = false;
      

      
      deletedEdges.forEach(edge => {
        // Support both new nodes format and old sections format
        // NEW FORMAT: node-{nodeIndex}-{nodeId} e.g., node-12-1770650633166-78mxvbcoh
        // OLD FORMAT: field-{sectionIndex}-{fieldIndex}-{fieldId}
        
        let sourceSectionIndex: number;
        let sourceFieldIndex: number;
        
        if (edge.source.startsWith('node-')) {
          // New format: node-{nodeIndex}-{rest of ID}
          const match = edge.source.match(/^node-(\d+)-/);
          if (!match) {
            console.log('[handleEdgesChange] Could not parse new format source:', edge.source);
            return;
          }
          sourceSectionIndex = parseInt(match[1]);
          sourceFieldIndex = 0; // Always 0 in new format
        } else if (edge.source.startsWith('field-')) {
          // Old format: field-{sectionIndex}-{fieldIndex}-{fieldId}
          const match = edge.source.match(/^field-(\d+)-(\d+)-/);
          if (!match) {
            console.log('[handleEdgesChange] Could not parse old format source:', edge.source);
            return;
          }
          sourceSectionIndex = parseInt(match[1]);
          sourceFieldIndex = parseInt(match[2]);
        } else {
          return;
        }
        
        const sourceField = updatedSections[sourceSectionIndex]?.fields?.[sourceFieldIndex];
        if (!sourceField) {
         
          return;
        }
        
       
        
        if (edge.sourceHandle && edge.sourceHandle.startsWith('option-')) {
          const optionIndex = parseInt(edge.sourceHandle.replace('option-', ''));
          
          if (sourceField.type === 'question' && sourceField.options && sourceField.options[optionIndex]) {
            sourceField.options[optionIndex] = {
              ...sourceField.options[optionIndex],
              next_section: null,
              next_field: null,
              next_dialog: null,
            };
            sectionsModified = true;
          }
        } else if (edge.sourceHandle === 'right' || edge.sourceHandle === 'next') {
          // Clear field-level navigation (handles both 'right' and 'next' handles)
          // 'right' is used by: dialog, context, end-text nodes
          // 'next' is used by: setter nodes
          sourceField.next_section = null;
          sourceField.next_field = null;
          sourceField.next_dialog = null;
          sectionsModified = true;
        } else if (edge.sourceHandle && edge.sourceHandle.startsWith('cond-option-')) {
          const optionIndex = parseInt(edge.sourceHandle.replace('cond-option-', ''));
          
          if (sourceField.conditionOptions && sourceField.conditionOptions[optionIndex]) {
            sourceField.conditionOptions[optionIndex] = {
              ...sourceField.conditionOptions[optionIndex],
              next_section: null,
              next_field: null,
              next_dialog: null,
              next_dialog_section: null,
            };
            sectionsModified = true;
          }
        }
      });
      
      if (sectionsModified) {
        setLocalFlowSections(updatedSections);
        localFlowSectionsRef.current = updatedSections;
        setHasUnsavedChanges(true);
        toast({
          title: 'Connection Removed',
          description: 'Connection data has been cleared',
        });
      } else {
        console.log('[handleEdgesChange] No sections were modified');
      }
    }
    
    setFlowEdges(edges);
    
    // Only set unsaved changes if we actually deleted edges or changed styles (structural change)
    // Edge additions/connections are tracked by the main useEffect via snapshot comparison
    // This prevents unnecessary unsaved change flags during internal React Flow updates
    if (deletedEdgeIds.length > 0 || styleChanges) {
      setHasUnsavedChanges(true);
    }
  }, [toast, setFlowEdges, setLocalFlowSections, setHasUnsavedChanges, localFlowSectionsRef]);
  
  // Handle connection end
  const handleConnectionEnd = useCallback((event: any, connectionState: any, flowPosition?: { x: number; y: number }) => {
    if (connectionState && !connectionState.toNode) {
      // Prevent the event from propagating to avoid triggering click-outside handlers
      if (event && event.stopPropagation) {
        event.stopPropagation();
      }
      if (event && event.preventDefault) {
        event.preventDefault();
      }
      
      const menuX = event?.clientX != null ? (zoom ? event.clientX / zoom : event.clientX) : 0;
      const menuY = event?.clientY != null ? (zoom ? event.clientY / zoom : event.clientY) : 0;
      const menuPosition = {
        x: menuX,
        y: menuY,
      };
      
      setContextMenu({
        show: true,
        position: menuPosition,
        sourceNode: connectionState.fromNode?.id,
        sourceHandle: connectionState.fromHandle?.id,
        flowPosition,
      });
    }
  }, [setContextMenu, zoom]);
  
  // Handle creating a new field from context menu
  const handleCreateFieldFromConnection = useCallback((fieldType: string) => {
    const newFieldId = generateId();
    const newField: DialogField = {
      id: newFieldId,
      type: fieldType as any,
      text: fieldType === 'external-dialog' ? '' : `New ${fieldType} field`,
      translatable:  fieldType !== 'condition' && fieldType !== 'setter' && fieldType !== 'external-dialog',
      order_index: 0,
      options: fieldType === 'question' ? [
        {
          id: generateId(),
          option_text: 'Option 1',
          translatable: true,
          order_index: 0,
        }
      ] : undefined,
      // Initialize condition fields with a default condition
      condition: fieldType === 'condition' ? {
        variable: '',
      } : undefined,
      // Initialize empty condition options array for condition fields
      conditionOptions: fieldType === 'condition' ? [] : undefined,
      // Initialize external-dialog fields with dialog reference
      next_dialog: fieldType === 'external-dialog' ? '' : undefined,
      next_dialog_section: fieldType === 'external-dialog' ? null : undefined,
    };
    
    if (contextMenu.sourceNode) {
      // Support both new nodes format and old sections format
      // NEW: node-{nodeIndex}-{nodeId} e.g., node-0-field_1770500636296_1
      // OLD: field-{sectionIndex}-{fieldIndex}-{fieldId}
      const match = contextMenu.sourceNode.match(/^(node|field)-(\d+)-(.+)$/);
      if (!match) return;
      
      const isNewFormat = match[1] === 'node';
      const sourceSectionIndex = parseInt(match[2]);
      // In new format, each section has exactly one field at index 0
      // In old format, match[3] is "{fieldIndex}-{fieldId}", so extract fieldIndex
      let sourceFieldIndex = 0;
      if (isNewFormat) {
        sourceFieldIndex = 0;
      } else {
        // Old format: match[3] is like "0-fieldId" or "1-fieldId"
        const fieldMatch = match[3].match(/^(\d+)-/);
        sourceFieldIndex = fieldMatch ? parseInt(fieldMatch[1]) : 0;
      }
      
      const updatedSections = deepClone(localFlowSectionsRef.current) as DialogSection[];
      
      // CRITICAL FIX: Find the next available branch number to avoid duplicates
      // Extract existing branch numbers from section names
      const existingBranchNumbers = updatedSections
        .map(s => {
          const match = s.name.match(/^Branch (\d+)$/);
          return match ? parseInt(match[1]) : null;
        })
        .filter((n): n is number => n !== null);
      
      const nextBranchNumber = existingBranchNumbers.length > 0 
        ? Math.max(...existingBranchNumbers) + 1 
        : updatedSections.length + 1;
      
      const newSectionId = generateId();
      const newSectionIdentifier = `section_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
      const newSection: DialogSection = {
        id: newSectionId,
        name: `Branch ${nextBranchNumber}`,
        section_identifier: newSectionIdentifier,
        order_index: updatedSections.length,
        fields: [newField],
      };
      
      updatedSections.push(newSection);
      const newSectionIndex = updatedSections.length - 1;
      
      // Connect source node to the new section
      const sourceField = updatedSections[sourceSectionIndex]?.fields?.[sourceFieldIndex];
      if (sourceField) {
        if (contextMenu.sourceHandle && contextMenu.sourceHandle.startsWith('option-')) {
          // Connection from a question option
          const optionIndex = parseInt(contextMenu.sourceHandle.replace('option-', ''));
          
          if (sourceField.type === 'question' && sourceField.options && sourceField.options[optionIndex]) {
            sourceField.options[optionIndex] = {
              ...sourceField.options[optionIndex],
              next_section: newSectionIdentifier,
              next_field: newFieldId,
              next_dialog: null,
              next_dialog_section: null,
            };
          }
        } else if (contextMenu.sourceHandle && contextMenu.sourceHandle.startsWith('cond-option-')) {
          // Connection from a condition option
          const optionIndex = parseInt(contextMenu.sourceHandle.replace('cond-option-', ''));
          
          if (sourceField.conditionOptions && sourceField.conditionOptions[optionIndex]) {
            sourceField.conditionOptions[optionIndex] = {
              ...sourceField.conditionOptions[optionIndex],
              next_section: newSectionIdentifier,
              next_field: newFieldId,
              next_dialog: null,
              next_dialog_section: null,
            };
          }
        } else {
          // Connection from a regular field (right handle, true handle, false handle, etc.)
          if (contextMenu.sourceHandle === 'true') {
            // Condition field - true branch (legacy, no next_field support)
            sourceField.next_section_true = newSectionIdentifier;
            sourceField.next_dialog_true = null;
            sourceField.next_dialog_section_true = null;
          } else if (contextMenu.sourceHandle === 'false') {
            // Condition field - false branch (legacy, no next_field support)
            sourceField.next_section_false = newSectionIdentifier;
            sourceField.next_dialog_false = null;
            sourceField.next_dialog_section_false = null;
          } else {
            // Regular field (right handle, next handle, or default)
            // This handles: dialog, context, end-text (right handle) and setter (next handle)
            sourceField.next_section = newSectionIdentifier;
            sourceField.next_field = newFieldId;
            sourceField.next_dialog = null;
            sourceField.next_dialog_section = null;
          }
        }
      }
      
      // Store pending position with the NEW node format (node-{nodeIndex}-{nodeId})
      // After migration, each section becomes one node, so newSectionIndex = newNodeIndex
      const newNodeId = `node-${newSectionIndex}-${newFieldId}`;
      if (contextMenu.flowPosition) {
        pendingNodePositionsRef.current[newNodeId] = contextMenu.flowPosition;
      }
      
      setLocalFlowSections(updatedSections);
      localFlowSectionsRef.current = updatedSections;
      setHasUnsavedChanges(true);
    } else {
      // Right-click on empty canvas (no source node)
      // CRITICAL FIX: Always create a new section for each right-clicked node
      // In flow mode, each section represents one visual node
      const updatedSections = [...localFlowSectionsRef.current];
      
      if (updatedSections.length === 0) {
        // First node - create Section 1
        const newSection: DialogSection = {
          id: generateId(),
          name: 'Section 1',
          section_identifier: generateSectionIdentifier(),
          order_index: 0,
          fields: [newField],
        };
        
        // Store position for the new node (first section, first node)
        const newNodeId = `node-0-${newFieldId}`;
        if (contextMenu.flowPosition) {
          pendingNodePositionsRef.current[newNodeId] = contextMenu.flowPosition;
        }
        
        setLocalFlowSections([newSection]);
        localFlowSectionsRef.current = [newSection];
      } else {
        // Subsequent nodes - create a new section (Branch N)
        // Find the next available branch number to avoid duplicates
        const existingBranchNumbers = updatedSections
          .map(s => {
            const match = s.name.match(/^Branch (\d+)$/);
            return match ? parseInt(match[1]) : null;
          })
          .filter((n): n is number => n !== null);
        
        const nextBranchNumber = existingBranchNumbers.length > 0 
          ? Math.max(...existingBranchNumbers) + 1 
          : updatedSections.length + 1;
        
        const newSectionId = generateId();
        const newSectionIdentifier = `section_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
        const newSection: DialogSection = {
          id: newSectionId,
          name: `Branch ${nextBranchNumber}`,
          section_identifier: newSectionIdentifier,
          order_index: updatedSections.length,
          fields: [newField],
        };
        
        updatedSections.push(newSection);
        const newSectionIndex = updatedSections.length - 1;
        
        // Store position for the new node with correct section index
        const newNodeId = `node-${newSectionIndex}-${newFieldId}`;
        if (contextMenu.flowPosition) {
          pendingNodePositionsRef.current[newNodeId] = contextMenu.flowPosition;
        }
        
        setLocalFlowSections(updatedSections);
        localFlowSectionsRef.current = updatedSections;
      }
      setHasUnsavedChanges(true);
    }
    
    setContextMenu({ show: false, position: { x: 0, y: 0 } });
    // Regenerate immediately (not with setTimeout) to ensure edges get proper styling
    regenerateFlowFromSections();
    
    toast({
      title: 'Field Added',
      description: `New ${fieldType} field added${contextMenu.sourceNode ? ' and connected' : ''}`,
    });
  }, [contextMenu, setLocalFlowSections, toast, regenerateFlowFromSections, localFlowSectionsRef, setHasUnsavedChanges, setContextMenu]);
  
  // Handle opening dialog jump selector from context menu
  const handleOpenDialogJumpSelector = useCallback(() => {
    if (contextMenu.sourceNode && contextMenu.sourceHandle) {
      setPendingJumpConnection({
        sourceNode: contextMenu.sourceNode,
        sourceHandle: contextMenu.sourceHandle,
      });
      setDialogJumpSelectorOpen(true);
    }
  }, [contextMenu, setPendingJumpConnection, setDialogJumpSelectorOpen]);
  
  // Handle dialog jump confirmation
  const handleDialogJumpConfirm = useCallback((dialogId: string, sectionIdentifier?: string | null) => {
    if (!pendingJumpConnection) return;
    
    const { sourceNode, sourceHandle } = pendingJumpConnection;
    
    // Support both new nodes format and old sections format
    const match = sourceNode.match(/(?:node|field)-(\d+)-(\d+)/);
    if (!match) return;
    
    const sourceSectionIndex = parseInt(match[1]);
    const sourceFieldIndex = parseInt(match[2]);
    
    const updatedSections = deepClone(localFlowSectionsRef.current) as DialogSection[];
    const sourceField = updatedSections[sourceSectionIndex]?.fields?.[sourceFieldIndex];
    if (!sourceField) return;
    
    if (sourceHandle && sourceHandle.startsWith('option-')) {
      const optionIndex = parseInt(sourceHandle.replace('option-', ''));
      
      if (sourceField.type === 'question' && sourceField.options && sourceField.options[optionIndex]) {
        sourceField.options[optionIndex] = {
          ...sourceField.options[optionIndex],
          next_dialog: dialogId,
          next_dialog_section: sectionIdentifier || null,
          next_section: null,
        };
        
        setLocalFlowSections(updatedSections);
        localFlowSectionsRef.current = updatedSections;
        setHasUnsavedChanges(true);
        // Regenerate immediately (not with setTimeout) to ensure edges get proper styling
        regenerateFlowFromSections();
        
        toast({
          title: 'Connection Created',
          description: `Option "${sourceField.options[optionIndex].option_text}" now jumps to external dialog`,
        });
      }
    }
    
    setPendingJumpConnection(null);
    setDialogJumpSelectorOpen(false);
  }, [pendingJumpConnection, setLocalFlowSections, toast, regenerateFlowFromSections, localFlowSectionsRef, setHasUnsavedChanges, setPendingJumpConnection, setDialogJumpSelectorOpen]);
  
  // Handle drag over for dropping dialogs onto canvas
  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);
  
  // Handle drop of dialogs onto canvas
  const handleDrop = useCallback((event: React.DragEvent, nodes: Node[]): Node | null => {
    event.preventDefault();
    
    const data = event.dataTransfer.getData('application/reactflow');
    if (!data) return null;
    
    try {
      const parsedData = JSON.parse(data);
      
      if (parsedData.itemType !== 'dialog') {
        toast({
          title: 'Cannot drop item',
          description: 'Only dialogs can be added to this dialog flow.',
          variant: 'destructive',
        });
        return null;
      }
      
      if (initialData?.id && parsedData.itemId === initialData.id) {
        toast({
          title: 'Cannot add dialog',
          description: 'A dialog cannot reference itself.',
          variant: 'destructive',
        });
        return null;
      }
      
      const dialogs = effectiveGameId ? getCachedDialogs(effectiveGameId) : [];
      const targetDialog = dialogs.find(d => d.id === parsedData.itemId);
      const firstSection = targetDialog?.data?.sections?.[0];
      
      const nodeId = `external-dialog-${parsedData.itemId}${firstSection?.section_identifier ? `-section-${firstSection.section_identifier}` : ''}`;
      
      const newNode: Node = {
        id: nodeId,
        type: 'externalDialog',
        position: { x: 0, y: 0 },
        data: {
          dialogId: parsedData.itemId,
          dialogName: targetDialog?.name || parsedData.label || 'Unknown Dialog',
          dialogKey: targetDialog?.dialog_key,
          sectionIdentifier: firstSection?.section_identifier,
          sectionName: firstSection?.name,
        },
      };
      
      setHasUnsavedChanges(true);
      
      toast({
        title: 'Dialog Added',
        description: `Added ${targetDialog?.name || parsedData.label} to canvas. Connect it to an option to create a jump.`,
      });
      
      return newNode;
    } catch (error) {
      console.error('Error handling drop:', error);
      return null;
    }
  }, [initialData?.id, effectiveGameId, toast, setHasUnsavedChanges]);

  return {
    handleFieldUpdate,
    handleSectionUpdate,
    regenerateFlowFromSections,
    handleConnect,
    handleNodesChange,
    handleEdgesChange,
    handleConnectionEnd,
    handlePaneContextMenu,
    handleCreateFieldFromConnection,
    handleOpenDialogJumpSelector,
    handleDialogJumpConfirm,
    handleDragOver,
    handleDrop,
    addCallbackToNodes,
  };
}

